import apiService from '../apiService';

/**
 * 取得測驗列表
 * @param {Object} params - 查詢參數
 * @param {number} params.page - 頁碼
 * @param {number} params.per_page - 每頁筆數
 * @returns {Promise} API 響應
 */
export const getQuizzes = async (params = {}) => {
    const url = '/api/admin/quizzes';
    return apiService.GET(url, params);
};

/**
 * 取得單筆測驗
 * @param {number} id - 測驗ID
 * @query {string} include - 包含的關聯資料 default, questions, answers
 * @returns {Promise} API 響應
 */
export const getSingleQuiz = async (quiz_id, include) => {
    let query = include ? `?include=${include}` : '';
    const url = `/api/admin/quizzes/${quiz_id}${query}`;
    return apiService.GET(url);
};

/**
 * 新增測驗
 * @param {Object} data - 測驗基本資料
 * @returns {Promise} API 響應
 */
export const createQuizzes = async (data) => {
    const url = '/api/admin/quizzes';
    return apiService.POST(url, data);
};

/**
 * 更新測驗
 * @param {Object} data - 測驗基本資料
 * @returns {Promise} API 響應
 */
export const updateQuizzes = async (quiz_id, data) => {
    const url = `/api/admin/quizzes/${quiz_id}`;
    return apiService.PUT(url, data);
};

/**
 * 刪除測驗
 * @returns {Promise} API 響應
 */
export const deleteQuizzes = async (quiz_id) => {
    const url = `/api/admin/quizzes/${quiz_id}`;
    return apiService.DELETE(url);
};

/**
 * 取得題目列表
 * @param {number} quiz_id - 測驗ID
 * @param {Object} params - 查詢參數
 * @param {string} params.include - 包含的關聯資料 (如: answers)
 * @returns {Promise} API 響應
 */
export const getQuestions = async (quiz_id, params = {}) => {
    const url = `/api/admin/quizzes/${quiz_id}/questions`;
    return apiService.GET(url, params);
};

/**
 * 新增題目
 * @param {Object} data - 題目資料
 * @returns {Promise} API 響應
 */
export const createQuestion = async (quiz_id, data) => {
    const url = `/api/admin/quizzes/${quiz_id}/questions`;
    return apiService.POST(url, data);
};

/**
 * 新增題目 (多數)
 * @param {Object} data - 題目資料
 * @returns {Promise} API 響應
 */
export const createBatchQuestions = async (quiz_id, data) => {
    const url = `/api/admin/quizzes/${quiz_id}/questions/batch`;
    return apiService.POST(url, data);
};

/**
 * 更新題目
 * @param {Object} data - 題目資料
 * @returns {Promise} API 響應
 */
export const updateQuestion = async (quiz_id, id, data) => {
    const url = `/api/admin/quizzes/${quiz_id}/questions/${id}`;
    return apiService.PUT(url, data);
};

/**
 * 刪除題目
 * @param {number} quiz_id - 測驗ID
 * @param {number} id - 題目ID
 * @returns {Promise} API 響應
 */
export const deleteQuestion = async (quiz_id, id) => {
    const url = `/api/admin/quizzes/${quiz_id}/questions/${id}`;
    return apiService.DELETE(url);
};

/**
 * 設定題目答案和答案
 * @param {Object} data - 題目資料
 * @returns {Promise} API 響應
 */
export const setAnswers = async (question_id, data) => {
    const url = `/api/admin/questions/${question_id}/answers`;
    return apiService.POST(url, data);
};

/**
 * 設定題目答案和答案 (多數)
 * @param {Object} data - 題目資料
 * @returns {Promise} API 響應
 */
export const setBatchAnswers = async (quiz_id, data) => {
    const url = `/api/admin/quizzes/${quiz_id}/answers`;
    return apiService.POST(url, data);
};

/**
 * 取得發布檢查報告
 * @param {number} quiz_id - 測驗ID
 * @returns {Promise} API 響應
 */
export const getPublishReport = async (quiz_id) => {
    const url = `/api/admin/quizzes/${quiz_id}/publish-report`;
    return apiService.GET(url);
};

/**
 * 創建維度（批量）
 * @param {number} quiz_id - 測驗ID
 * @param {Object} data - 維度資料
 * @returns {Promise} API 響應
 */
export const createDimensions = async (quiz_id, data) => {
    const url = `/api/admin/quizzes/${quiz_id}/dimensions`;
    return apiService.POST(url, data);
};

/**
 * 更新維度
 * @param {number} id - 維度ID
 * @param {Object} data - 維度資料
 * @returns {Promise} API 響應
 */
export const updateDimension = async (id, data) => {
    const url = `/api/admin/quiz-dimensions/${id}`;
    return apiService.PUT(url, data);
};

/**
 * 刪除維度
 * @param {number} id - 維度ID
 * @returns {Promise} API 響應
 */
export const deleteDimension = async (id) => {
    const url = `/api/admin/quiz-dimensions/${id}`;
    return apiService.DELETE(url);
};

/**
 * 批量設定選項權重（維度加權）
 * @param {number} quiz_id - 測驗ID
 * @param {Object} data - 選項權重資料
 * @returns {Promise} API 響應
 */
export const setBatchOptionWeights = async (quiz_id, data) => {
    const url = `/api/admin/quizzes/${quiz_id}/option-weights`;
    return apiService.POST(url, data);
};

/**
 * 創建測驗結果卡
 * @param {FormData|Object} data - 結果卡資料（可以是 FormData 或普通物件）
 * @returns {Promise} API 響應
 */
export const createQuizResult = async (data) => {
    const url = '/api/admin/quiz-results';
    // FormData 時，apiService 會自動處理 Content-Type
    return apiService.POST(url, data);
};

/**
 * 獲取測驗結果卡列表
 * @param {number} quiz_id - 測驗ID
 * @returns {Promise} API 響應
 */
export const getQuizResults = async (quiz_id) => {
    const url = '/api/admin/quiz-results';
    return apiService.GET(url, { quiz_id });
};

/**
 * 更新測驗結果卡
 * @param {number} result_id - 結果卡ID
 * @param {FormData} data - 結果卡資料（FormData，支援檔案上傳）
 * @returns {Promise} API 響應
 */
export const updateQuizResult = async (result_id, data) => {
    const url = `/api/admin/quiz-results/${result_id}`;
    // 使用 POST 方法配合 _method=PUT（推薦，用於檔案上傳）
    // 確保 FormData 中包含 _method=PUT
    if (data instanceof FormData && !data.has('_method')) {
        data.append('_method', 'PUT');
    }
    return apiService.POST(url, data);
};

/**
 * 刪除測驗結果卡
 * @param {number} result_id - 結果卡ID
 * @returns {Promise} API 響應
 */
export const deleteQuizResult = async (result_id) => {
    const url = `/api/admin/quiz-results/${result_id}`;
    return apiService.DELETE(url);
};

/**
 * 獲取測驗結果卡圖片 URL
 * @param {number} result_id - 結果卡ID
 * @returns {string} 圖片的 API URL（使用 window.location.origin 確保絕對路徑）
 */
export const getQuizResultImage = (result_id) => {
    const url = `/api/admin/quiz-results/${result_id}/image`;

    // 使用 window.location.origin 確保是絕對路徑，避免相對路徑問題
    // 這樣即使在不同路由下也能正確請求
    return `${window.location.origin}${url}`;
};
